// src/gtm.client.ts
/**
 * Google Tag Manager — DEMO SESSIONS ONLY.
 *
 * Why this is gated rather than loaded globally: paying customers' use of the admin app
 * is not measured here at all. Product analytics for live usage is collected API-side.
 * The gate means a real customer never DOWNLOADS the GTM script — not "loads it and
 * sends nothing" — so there is no third-party request, no cookie, and no bundle cost on
 * their session.
 *
 * Why the JWT claim and not `?demo=1`: the query param survives exactly one navigation
 * and any visitor can type it. `demo` is a signed claim minted server-side by
 * `_mint_demo_token` (identity_service/app/api/endpoints/demo.py), so it cannot be
 * spoofed into existence and it survives reloads for the life of the 8h session.
 *
 * What it is for: `id.boona.app/demo` is a 302 that renders no HTML, so the demo step
 * was invisible in GA4 — a whole branch of the paid funnel ("Живое демо в один клик" is
 * a sitelink on every campaign) simply had no data. Same container and GA4 property as
 * the marketing site on purpose: admin.boona.app and boona.app share the registrable
 * domain, so the `_ga` cookie is already shared and the ad click → landing → demo path
 * reads as ONE session with no cross-domain linking.
 *
 * ⚠ CONSENT IS NOT YET WIRED (owner decision, 2026-08-26 — deliberately deferred until
 * tracking is confirmed end-to-end). The marketing site's cookie-consent.js sets Google's
 * kill switch and denies analytics_storage on opt-out; this module does NOT read that
 * signal, so a visitor who declined on boona.app currently still gets GTM here. That is a
 * consent bypass and must be closed before this is considered finished.
 * See boona docs/SEO/ads/analytics-journeys.md.
 */

const GTM_ID = import.meta.env.VITE_GTM_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

let injected = false;

/**
 * Whether THIS session is a demo, as last asserted by a caller holding the token.
 *
 * Separate from `injected` on purpose. `injected` records that the script was downloaded
 * and can never be undone; this records the current truth. A visitor who demoes and then
 * signs up arrives with a real token, and everything below must go quiet even if an
 * earlier call already injected the script.
 */
let demoConfirmed = false;

/**
 * Inject GTM once, for a demo session only. Safe to call repeatedly and from more than
 * one entry path (fresh SSO landing and bootstrap-after-reload both call it).
 */
export function initGtmForDemo(isDemo: boolean): void {
  // Record the verdict FIRST and unconditionally: a later `false` from a real token must
  // override an earlier `true` from a stale flag, even though the script cannot be undone.
  demoConfirmed = isDemo;
  if (injected) return;
  if (!isDemo) return;
  if (!GTM_ID) {
    console.info("[GTM] demo session, but VITE_GTM_ID is not set — skipping");
    return;
  }
  if (typeof window === "undefined" || typeof document === "undefined") return;

  injected = true;
  window.dataLayer = window.dataLayer || [];
  // Marks every event from this session so demo traffic stays separable in GA4 from the
  // marketing site's, while still sharing the property that makes the funnel continuous.
  window.dataLayer.push({ demo_mode: true });
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(script);

  // Covers the fresh SSO landing AND the re-arm after a reload, so in-app behaviour is
  // measured for the whole 8h demo session rather than only the first page.
  bindDemoListeners();
  console.info(`[GTM] loaded for demo session (${GTM_ID})`);
}

/**
 * The demo JWT carries no locale claim, but /demo sets `boona_locale` on `.boona.app`
 * with httponly=False precisely so the client can read it (demo.py). Read it there
 * rather than inventing a claim, so this stays in step with the backend.
 */
function readLocaleCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const hit = document.cookie
    .split("; ")
    .find((c) => c.startsWith("boona_locale="));
  return hit ? decodeURIComponent(hit.slice("boona_locale=".length)) : undefined;
}

/**
 * Demo session bookkeeping, kept in localStorage rather than memory because the whole
 * point is to survive the navigation that a reload or a Back-button bounce performs.
 */
const SID_KEY = "boona_demo_sid";
const SID_AT_KEY = "boona_demo_sid_at";

function readStore(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // Safari private mode throws on access, not just on write.
  }
}

function writeStore(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* nothing to do — the event still fires, only the re-entry flag is lost */
  }
}

/**
 * Announce the demo entry itself — the funnel step that the 302 made invisible.
 *
 * `sid` is the demo session claim from the JWT. It is NEW on every hit of
 * `id.boona.app/demo`, which is what makes it the right re-entry detector: seeing a
 * second, different sid in the same browser means the visitor went through /demo again
 * and was handed a fresh demo session, losing wherever they were. That is precisely the
 * history-trap symptom (docs/SEO/ads/analytics-journeys.md) — before this, a re-entry and
 * a first entry were indistinguishable in GA4 and the loop only showed up in a path report.
 */
export function trackDemoStart(region?: string | null, sid?: string | null): void {
  // `injected` alone was the wrong guard: it stayed true for a real customer whose session
  // had already been re-classified, so demo_start fired carrying their REAL session id and
  // polluted the demo funnel with paying users.
  if (!injected || !demoConfirmed) return;

  const prev = readStore(SID_KEY);
  const prevAt = Number(readStore(SID_AT_KEY) || 0);
  const now = Date.now();

  let entry: "first" | "resumed" | "reentry" = "first";
  if (prev && sid && prev === sid) entry = "resumed";
  else if (prev) entry = "reentry";

  const sincePrev = prevAt ? Math.round((now - prevAt) / 1000) : undefined;

  if (sid) writeStore(SID_KEY, sid);
  writeStore(SID_AT_KEY, String(now));

  window.dataLayer?.push({
    event: "demo_start",
    demo_locale: readLocaleCookie(),
    demo_region: region || undefined,
    demo_sid: sid || undefined,
    demo_entry_type: entry,
    demo_seconds_since_previous: sincePrev,
  });

  // A separate event as well as the parameter, so GA4 can key a funnel step or an
  // audience straight off it without needing a custom dimension registered first.
  if (entry === "reentry") {
    window.dataLayer?.push({
      event: "demo_reentry",
      demo_sid: sid || undefined,
      demo_seconds_since_previous: sincePrev,
    });
  }

  console.info(`[GTM] demo_start pushed (${entry})`);
}

/** Best available stable label for a clicked element, preferring attributes over text. */
function describe(el: Element): Record<string, string | undefined> {
  const actionable = el.closest("a,button,[role=button],[data-testid]") ?? el;
  const text = (actionable.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60);
  return {
    demo_click_test_id: actionable.getAttribute("data-testid") || undefined,
    demo_click_id: actionable.id || undefined,
    demo_click_label:
      actionable.getAttribute("aria-label") || actionable.getAttribute("title") || text || undefined,
    demo_click_href: (actionable as HTMLAnchorElement).href || undefined,
    demo_click_tag: actionable.tagName.toLowerCase(),
  };
}

let listenersBound = false;

/**
 * Set just before the dashboard is opened, so the `demo_exit` that the resulting
 * visibility change triggers can say what really happened.
 *
 * `goToDashboard` uses window.open, which backgrounds this tab without unloading it.
 * `visibilitychange -> hidden` cannot tell that apart from the visitor closing the laptop,
 * and the dashboard is reached from seven links including the onboarding card — so
 * untagged, the most common in-product journey would read as an exit.
 */
const HANDOFF_WINDOW_MS = 5000;
let dashboardHandoffAt = 0;

export function noteDashboardHandoff(): void {
  dashboardHandoffAt = Date.now();
}

/**
 * What the visitor actually DID inside the demo.
 *
 * `admin.boona.app` had never emitted a single click event, so there was no way to tell
 * whether anyone tried to convert from inside the product — 14% of paid clicks reached the
 * demo and 0% reached a signup page, with nothing in between to explain it.
 *
 * Bound here rather than in a component so it covers both entry paths (fresh SSO landing
 * and re-arm after reload) and every route, with no per-screen wiring.
 */
function bindDemoListeners(): void {
  if (listenersBound) return;
  listenersBound = true;
  const startedAt = Date.now();

  document.addEventListener(
    "click",
    (e) => {
      if (!demoConfirmed) return;   // re-classified mid-session: stop reporting
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      window.dataLayer?.push({ event: "demo_click", ...describe(target) });
    },
    { capture: true, passive: true },
  );

  // Leaving is the hardest event to catch. `beforeunload` is skipped outright by mobile
  // Safari, and `pagehide` does not fire when a tab is merely backgrounded — which is
  // exactly what happens here, because goToDashboard() opens the dashboard with
  // window.open. `visibilitychange -> hidden` is the one signal every browser guarantees,
  // so it leads and pagehide is the desktop backstop. Whichever arrives first wins.
  let exitSent = false;
  const sendExit = (reason: string) => {
    if (exitSent || !demoConfirmed) return;
    exitSent = true;
    // A hand-off to the dashboard also hides this tab, so without this every dashboard
    // visit would be recorded as the visitor leaving. They have not left — the dashboard
    // opens in a SECOND tab and this one stays alive behind it.
    if (Date.now() - dashboardHandoffAt < HANDOFF_WINDOW_MS) reason = "to_dashboard";
    window.dataLayer?.push({
      event: "demo_exit",
      demo_seconds_in_app: Math.round((Date.now() - startedAt) / 1000),
      demo_exit_path: window.location.pathname,
      demo_exit_reason: reason,
    });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendExit("hidden");
  });
  window.addEventListener("pagehide", () => sendExit("pagehide"));
}
